import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosResponse } from 'axios';
import { Authorities } from 'src/models/authorizations.model';
import { Pagination } from 'src/models/pagination.model';
import { CreateUserBody, User } from 'src/models/user.model';
import { closeModal } from './modal.reducer';

type InitialState = {
  loading: boolean;
  failure: boolean;
  success: boolean;
  fetchLoading: boolean;
  fetchFailure: boolean;
  fetchSuccess: boolean;
  searchLoading: boolean;
  searchFailure: boolean;
  searchSuccess: boolean;
  createLoading: boolean;
  createSuccess: boolean;
  createFailure: boolean;
  user: User;
  users: User[];
  totalItems: number;
  refresh: number;
  newEmployee: User;
};

const INITIAL_STATE: InitialState = {
  loading: false,
  failure: false,
  success: false,
  createLoading: false,
  createSuccess: false,
  createFailure: false,
  fetchLoading: false,
  fetchFailure: false,
  fetchSuccess: false,
  searchLoading: false,
  searchFailure: false,
  searchSuccess: false,
  user: null as unknown as User,
  users: [] as User[],
  totalItems: 0,
  refresh: 0,
  newEmployee: null as unknown as User,
};

export const createNewUser = createAsyncThunk('user-create/post', async (data: CreateUserBody, { rejectWithValue, dispatch }) => {
  try {
    const response: AxiosResponse<User> = await axios.post(`/api/user/create`, data);

    dispatch(refreshUsers());
    dispatch(closeModal());

    return response.data;
  } catch (error: any) {
    if (error.response.data === 'User already exists') {
      dispatch(closeModal());
    }
    return rejectWithValue(error.message);
  }
});

export const getUsers = createAsyncThunk('users/get', async (data: { pagination: Pagination }, { rejectWithValue }) => {
  try {
    const response: AxiosResponse<{ data: User[]; totalItems: number }> = await axios.get(`/api/user`, {
      params: data.pagination,
    });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const searchCapabilitiesFromRoleId = createAsyncThunk(
  'capabilities/post',
  async (data: { roleId: number; pagination: Pagination }, { rejectWithValue }) => {
    try {
      const response: AxiosResponse<Authorities[]> = await axios.post(
        `/api/auth/me/role`,
        { roleId: data.roleId },
        { params: data.pagination }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const searchUserById = createAsyncThunk('user/post', async (data: { id: number }, { rejectWithValue }) => {
  try {
    const response: AxiosResponse<User> = await axios.post(`/api/user`, data);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const userSlice = createSlice({
  name: 'get/users',
  initialState: INITIAL_STATE,
  reducers: {
    resetUserState: () => {
      return INITIAL_STATE;
    },
    refreshUsers: (state: InitialState) => {
      return {
        ...state,
        refresh: state.refresh + 1,
      };
    },
  },
  extraReducers: builder => {
    builder.addCase(getUsers.pending, (state: InitialState) => {
      return {
        ...state,
        loading: true,
        failure: false,
        success: false,
      };
    });
    builder.addCase(
      getUsers.fulfilled,
      (
        state: InitialState,
        action: PayloadAction<{
          data: User[];
          totalItems: number;
        }>
      ) => {
        return {
          ...state,
          loading: false,
          failure: false,
          success: true,
          users: action.payload.data,
          totalItems: action.payload.totalItems,
        };
      }
    );
    builder.addCase(getUsers.rejected, (state: InitialState) => {
      return {
        ...state,
        loading: false,
        failure: true,
        success: false,
      };
    });
    builder.addCase(searchCapabilitiesFromRoleId.pending, (state: InitialState) => {
      return {
        ...state,
        fetchLoading: true,
        fetchFailure: false,
        fetchSuccess: false,
      };
    });
    builder.addCase(searchCapabilitiesFromRoleId.fulfilled, (state: InitialState, action: PayloadAction<Authorities[]>) => {
      return {
        ...state,
        fetchLoading: false,
        fetchFailure: false,
        fetchSuccess: true,
        capabilities: action.payload,
      };
    });
    builder.addCase(searchCapabilitiesFromRoleId.rejected, (state: InitialState) => {
      return {
        ...state,
        fetchLoading: false,
        fetchFailure: true,
        fetchSuccess: false,
      };
    });
    builder.addCase(searchUserById.pending, (state: InitialState) => {
      return {
        ...state,
        searchLoading: true,
        searchFailure: false,
        searchSuccess: false,
      };
    });
    builder.addCase(searchUserById.fulfilled, (state: InitialState, action: PayloadAction<User>) => {
      return {
        ...state,
        searchLoading: false,
        searchFailure: false,
        searchSuccess: true,
        user: action.payload,
      };
    });
    builder.addCase(searchUserById.rejected, (state: InitialState) => {
      return {
        ...state,
        searchLoading: false,
        searchFailure: true,
        searchSuccess: false,
      };
    });
    builder.addCase(createNewUser.pending, (state): InitialState => {
      return {
        ...state,
        createLoading: true,
        createSuccess: false,
        createFailure: false,
      };
    });
    builder.addCase(createNewUser.fulfilled, (state, action: PayloadAction<User>): InitialState => {
      return {
        ...state,
        createLoading: false,
        createSuccess: true,
        createFailure: false,
        newEmployee: action.payload,
      };
    });
    builder.addCase(createNewUser.rejected, (state): InitialState => {
      return {
        ...state,
        createLoading: false,
        createSuccess: false,
        createFailure: true,
      };
    });
  },
});

export const { resetUserState, refreshUsers } = userSlice.actions;

export default userSlice.reducer;
